import { useState } from 'react'
import { useUser } from '@clerk/clerk-react'
import { useWorkspaceStore } from '../../stores/workspaceStore'
import { governanceApi, EmailTestType, SmsTestType } from '../../services/api'
import { Alert, Button, Card, CardFooter, CardHeader, Field, FieldGrid, Icon, SegmentedControl, Spacer, Stack } from '../../ds'

type Channel = 'email' | 'sms'

const EMAIL_TYPES: { value: EmailTestType; label: string; description: string; minProducts: number }[] = [
  { value: 'cart_recovery', label: 'Cart Recovery', description: 'Abandoned cart reminder', minProducts: 1 },
  { value: 'browse_followup', label: 'Browse Follow-Up', description: 'Products they viewed', minProducts: 1 },
  { value: 'comparison', label: 'Product Comparison', description: 'Side-by-side comparison', minProducts: 2 },
  { value: 'inspiration', label: 'Product Inspiration', description: 'Personalised recommendations', minProducts: 1 },
  { value: 'back_in_stock', label: 'Back in Stock', description: 'Item availability alert', minProducts: 1 },
  { value: 'price_drop', label: 'Price Drop', description: 'Price reduction alert', minProducts: 1 },
]

const SMS_TYPES: { value: SmsTestType; label: string; description: string }[] = [
  { value: 'cart_recovery', label: 'Cart Recovery', description: 'Abandoned cart nudge' },
  { value: 'back_in_stock', label: 'Back in Stock', description: 'Item availability alert' },
  { value: 'price_drop', label: 'Price Drop', description: 'Price reduction alert' },
  { value: 'browse_followup', label: 'Browse Follow-Up', description: 'Post-browse nudge' },
]

/**
 * Test Messaging — send a real test email or SMS for any touchpoint type.
 *
 * Reskinned to the Playbook design system (.ms-* primitives). The merchant
 * picks a channel + touchpoint type, supplies Shopify product IDs (and
 * optionally a customer ID for real personalisation), and Marva generates and
 * sends a live message with AI content. SMS replies start a two-way thread.
 */
const TestMessagingSection = () => {
  const { currentWorkspace } = useWorkspaceStore()
  const { user } = useUser()

  const [channel, setChannel] = useState<Channel>('email')

  // Email state
  const [emailType, setEmailType] = useState<EmailTestType>('cart_recovery')
  const [recipientEmail, setRecipientEmail] = useState(user?.primaryEmailAddress?.emailAddress || '')
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [customerFirstName, setCustomerFirstName] = useState('')
  const [oldPrice, setOldPrice] = useState('')
  const [newPrice, setNewPrice] = useState('')

  // SMS state
  const [smsType, setSmsType] = useState<SmsTestType>('cart_recovery')
  const [recipientPhone, setRecipientPhone] = useState('')

  // Shared state
  const [productIds, setProductIds] = useState('')
  const [customerId, setCustomerId] = useState('')
  const [isSending, setIsSending] = useState(false)
  const [result, setResult] = useState<{ success: boolean; message: string } | null>(null)

  const selectedEmailType = EMAIL_TYPES.find((t) => t.value === emailType)

  const handleSendEmail = async () => {
    if (!currentWorkspace) return

    const productIdList = productIds.split(',').map((id) => id.trim()).filter(Boolean)
    if (productIdList.length === 0) {
      setResult({ success: false, message: 'Please enter at least one product ID' })
      return
    }
    if (selectedEmailType && productIdList.length < selectedEmailType.minProducts) {
      setResult({ success: false, message: `${selectedEmailType.label} requires at least ${selectedEmailType.minProducts} product(s)` })
      return
    }
    if (!recipientEmail || !recipientEmail.includes('@')) {
      setResult({ success: false, message: 'Please enter a valid email address' })
      return
    }

    setIsSending(true)
    setResult(null)

    try {
      const context: Record<string, unknown> = {}
      if (customerFirstName) context.customerFirstName = customerFirstName
      if (emailType === 'price_drop') {
        if (oldPrice) context.oldPrice = Math.round(parseFloat(oldPrice) * 100)
        if (newPrice) context.newPrice = Math.round(parseFloat(newPrice) * 100)
      }

      const response = await governanceApi.sendTestEmail(currentWorkspace.id, {
        emailType,
        recipientEmail,
        productIds: productIdList,
        customerId: customerId || undefined,
        context: Object.keys(context).length > 0 ? context : undefined,
      })

      if (response.success) {
        setResult({ success: true, message: `Test email sent to ${recipientEmail}! Subject: "${response.data?.subject}"` })
      } else {
        setResult({ success: false, message: response.error || 'Failed to send test email' })
      }
    } catch (error) {
      setResult({ success: false, message: error instanceof Error ? error.message : 'Failed to send test email' })
    } finally {
      setIsSending(false)
    }
  }

  const handleSendSms = async () => {
    if (!currentWorkspace) return

    const productIdList = productIds.split(',').map((id) => id.trim()).filter(Boolean)
    if (productIdList.length === 0) {
      setResult({ success: false, message: 'Please enter at least one product ID' })
      return
    }
    if (!recipientPhone || !/^\+[1-9]\d{6,14}$/.test(recipientPhone)) {
      setResult({ success: false, message: 'Please enter a valid phone number in E.164 format (e.g. +12025551234)' })
      return
    }

    setIsSending(true)
    setResult(null)

    try {
      const context: Record<string, unknown> = {}
      if (customerFirstName) context.customerFirstName = customerFirstName

      const response = await governanceApi.sendTestSms(currentWorkspace.id, {
        smsType,
        recipientPhone,
        productIds: productIdList,
        customerId: customerId || undefined,
        context: Object.keys(context).length > 0 ? (context as { customerFirstName?: string }) : undefined,
      })

      if (response.success) {
        const maskedPhone = recipientPhone.replace(/(\+\d{1,3})\d+(\d{4})/, '$1***$2')
        setResult({ success: true, message: `Test SMS sent to ${maskedPhone}! Reply from your phone to test the conversation.` })
      } else {
        setResult({ success: false, message: response.error || 'Failed to send test SMS' })
      }
    } catch (error) {
      setResult({ success: false, message: error instanceof Error ? error.message : 'Failed to send test SMS' })
    } finally {
      setIsSending(false)
    }
  }

  const handleSend = channel === 'email' ? handleSendEmail : handleSendSms

  const val = (e: { target: unknown }) => (e.target as HTMLInputElement).value

  return (
    <Card>
      <CardHeader
        title="Test messages"
        description="Send a real test email or SMS to preview how any touchpoint looks. Marva generates personalised content from your brand voice — SMS replies start a two-way conversation."
      />

      <Stack>
        {/* Channel selector — one of two values, so a radiogroup (D-18), not tabs. Sized to its
            options like every other segmented control; a Stack would stretch it to full width. */}
        <SegmentedControl
          label="Test channel"
          options={[{ value: 'email', label: 'Email' }, { value: 'sms', label: 'SMS' }]}
          value={channel}
          onChange={(v) => { setChannel(v as typeof channel); setResult(null) }}
        />

        {/* Type */}
        {channel === 'email' ? (
          <Field as="select" label="Email type" value={emailType} onChange={(e) => setEmailType(val(e) as EmailTestType)}>
            {EMAIL_TYPES.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label} — {type.description}
              </option>
            ))}
          </Field>
        ) : (
          <Field as="select" label="SMS type" value={smsType} onChange={(e) => setSmsType(val(e) as SmsTestType)}>
            {SMS_TYPES.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label} — {type.description}
              </option>
            ))}
          </Field>
        )}

        {/* Product IDs */}
        <Field
          label={`Shopify product IDs (comma-separated${channel === 'email' && selectedEmailType ? `, min ${selectedEmailType.minProducts}` : ''})`}
          value={productIds}
          onChange={(e) => setProductIds(val(e))}
          placeholder="e.g. 7654321098765, 8765432109876"
          hint="Find product IDs in Shopify Admin under Products."
        />

        {/* Customer ID */}
        <Field
          label="Shopify customer ID"
          optional
          value={customerId}
          onChange={(e) => setCustomerId(val(e))}
          placeholder="e.g. 1234567890123"
          hint="Include to test with real customer data (purchase history, preferences)."
        />

        {/* Recipient */}
        {channel === 'email' ? (
          <Field
            label="Send to"
            type="email"
            autoComplete="email"
            value={recipientEmail}
            onChange={(e) => setRecipientEmail(val(e))}
            placeholder="you@example.com"
          />
        ) : (
          <Field
            label="Send to"
            type="tel"
            autoComplete="tel"
            value={recipientPhone}
            onChange={(e) => setRecipientPhone(val(e))}
            placeholder="+12025551234"
            hint="E.164 format required (e.g. +12025551234). Reply from your phone to test two-way conversations."
          />
        )}

        {/* Advanced */}
        <div>
          <Button
            variant="ghost"
            size="sm"
            aria-expanded={showAdvanced}
            icon={<Icon d={showAdvanced ? 'M18 15l-6-6-6 6' : 'M6 9l6 6 6-6'} />}
            onClick={() => setShowAdvanced((v) => !v)}
          >
            Advanced options
          </Button>
        </div>

        {showAdvanced && (
          <Card tone="recessed" padding="sm">
            <Stack gap="sm">
              <Field
                label="Customer first name (override)"
                value={customerFirstName}
                onChange={(e) => setCustomerFirstName(val(e))}
                placeholder="Jane"
              />

              {channel === 'email' && emailType === 'price_drop' && (
                <FieldGrid>
                  <Field
                    label="Old price ($)"
                    type="number"
                    prefix="$"
                    value={oldPrice}
                    onChange={(e) => setOldPrice(val(e))}
                    placeholder="99.99"
                    step="0.01"
                  />
                  <Field
                    label="New price ($)"
                    type="number"
                    prefix="$"
                    value={newPrice}
                    onChange={(e) => setNewPrice(val(e))}
                    placeholder="79.99"
                    step="0.01"
                  />
                </FieldGrid>
              )}
            </Stack>
          </Card>
        )}

        {result && (
          <Alert tone={result.success ? 'success' : 'danger'} live={result.success ? 'polite' : 'assertive'}>
            {result.message}
          </Alert>
        )}
      </Stack>

      <CardFooter>
        <Spacer />
        <Button
          size="sm"
          icon={<Icon d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />}
          busy={isSending}
          busyLabel="Sending…"
          disabled={!currentWorkspace}
          onClick={handleSend}
        >
          {`Send test ${channel === 'email' ? 'email' : 'SMS'}`}
        </Button>
      </CardFooter>
    </Card>
  )
}

export default TestMessagingSection
